/**
 * UNIFIED CAPABILITY HOOK
 * 
 * Single source of truth for permission checks.
 * All UI permission decisions MUST go through this hook.
 * 
 * @security This is a security-critical file. Changes require review.
 */

import { useCallback } from 'react';
import { useCurrentRole } from '@/lib/auth/roles';
import { 
  Capability, 
  roleHasCapability, 
  roleHasAllCapabilities, 
  roleHasAnyCapability,
  canAccessRoute,
  isStaffRole,
  isAdminRole,
  ROLE_CAPABILITIES,
} from '@/lib/auth/capabilities';
import type { OrgRole } from '@/lib/auth/roles';

export interface UseCapabilityReturn {
  /** Current user role */
  role: OrgRole | null;
  /** Whether user is super admin */
  isSuperAdmin: boolean;
  /** Whether permissions are still loading */
  isLoading: boolean;
  
  /** Check if user has a specific capability */
  hasCapability: (capability: Capability) => boolean;
  /** Check if user has ALL specified capabilities */
  hasAllCapabilities: (capabilities: Capability[]) => boolean;
  /** Check if user has ANY of the specified capabilities */
  hasAnyCapability: (capabilities: Capability[]) => boolean;
  /** Check if user can access a specific route */
  canAccess: (route: string) => boolean;
  
  /** Whether user is staff (not player) */
  isStaff: boolean;
  /** Whether user is admin (org_admin or super_admin) */
  isAdmin: boolean;
  
  // Common capability shortcuts
  canReadPlayers: boolean;
  canWritePlayers: boolean;
  canReadWellness: boolean;
  canWriteWellness: boolean;
  canReadInjuries: boolean;
  canWriteInjuries: boolean;
  canDeleteInjuries: boolean;
  canReadRTP: boolean;
  canWriteRTP: boolean;
  canAdvanceRTP: boolean;
  canUpload: boolean;
  canManageOrg: boolean;
  canManageTeam: boolean;
  canManageUsers: boolean;
  canViewDashboard: boolean;
  canViewAnalytics: boolean;
  canViewInsights: boolean;
  canOverrideGuardrails: boolean;
  canDismissRiskFlags: boolean;
}

/**
 * Unified hook for all capability checks.
 * 
 * Usage:
 * ```tsx
 * const { hasCapability, canWriteInjuries, isStaff } = useCapability();
 * 
 * if (hasCapability('write:injuries')) {
 *   // Can write injuries
 * }
 * ```
 */
export function useCapability(): UseCapabilityReturn {
  const { role, isSuperAdmin, isLoading } = useCurrentRole();
  
  const hasCapability = useCallback(
    (capability: Capability): boolean => {
      if (isSuperAdmin) return true;
      return roleHasCapability(role, capability);
    },
    [role, isSuperAdmin]
  );
  
  const hasAllCapabilities = useCallback(
    (capabilities: Capability[]): boolean => {
      if (isSuperAdmin) return true;
      return roleHasAllCapabilities(role, capabilities);
    },
    [role, isSuperAdmin]
  );
  
  const hasAnyCapability = useCallback(
    (capabilities: Capability[]): boolean => {
      if (isSuperAdmin) return true;
      return roleHasAnyCapability(role, capabilities);
    },
    [role, isSuperAdmin]
  );
  
  const canAccess = useCallback(
    (route: string): boolean => {
      if (isSuperAdmin) return true;
      return canAccessRoute(role, route);
    },
    [role, isSuperAdmin]
  );
  
  return {
    role,
    isSuperAdmin,
    isLoading,
    
    hasCapability,
    hasAllCapabilities,
    hasAnyCapability,
    canAccess,
    
    isStaff: isSuperAdmin || isStaffRole(role),
    isAdmin: isSuperAdmin || isAdminRole(role),
    
    // Pre-computed common capabilities
    canReadPlayers: isSuperAdmin || roleHasCapability(role, 'read:players'),
    canWritePlayers: isSuperAdmin || roleHasCapability(role, 'write:players'),
    canReadWellness: isSuperAdmin || roleHasCapability(role, 'read:wellness'),
    canWriteWellness: isSuperAdmin || roleHasCapability(role, 'write:wellness'),
    canReadInjuries: isSuperAdmin || roleHasCapability(role, 'read:injuries'),
    canWriteInjuries: isSuperAdmin || roleHasCapability(role, 'write:injuries'),
    canDeleteInjuries: isSuperAdmin || roleHasCapability(role, 'delete:injuries'),
    canReadRTP: isSuperAdmin || roleHasCapability(role, 'read:rtp_cases'),
    canWriteRTP: isSuperAdmin || roleHasCapability(role, 'write:rtp_cases'),
    canAdvanceRTP: isSuperAdmin || roleHasCapability(role, 'advance:rtp_stage'),
    canUpload: isSuperAdmin || roleHasCapability(role, 'upload:csv'),
    canManageOrg: isSuperAdmin || roleHasCapability(role, 'manage:org_settings'),
    canManageTeam: isSuperAdmin || roleHasCapability(role, 'manage:team_settings'),
    canManageUsers: isSuperAdmin || roleHasCapability(role, 'manage:users'),
    canViewDashboard: isSuperAdmin || roleHasCapability(role, 'view:dashboard'),
    canViewAnalytics: isSuperAdmin || roleHasCapability(role, 'view:analytics'),
    canViewInsights: isSuperAdmin || roleHasCapability(role, 'view:insights'),
    canOverrideGuardrails: isSuperAdmin || roleHasCapability(role, 'override:guardrails'),
    canDismissRiskFlags: isSuperAdmin || roleHasCapability(role, 'dismiss:risk_flags'),
  };
}

/**
 * Shorthand hook for a single capability check.
 */
export function useHasCapability(capability: Capability): boolean {
  const { hasCapability } = useCapability();
  return hasCapability(capability);
}

/**
 * Shorthand hook for route access check.
 */
export function useCanAccessRoute(route: string): boolean {
  const { canAccess } = useCapability();
  return canAccess(route);
}
